import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, MapPin, CheckCircle, Clock, Building, Heart, Star, Send } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export const CampsPage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;
  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    eventDate: '',
    expectedParticipants: '',
    venueAddress: '',
    specialRequirements: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Camp request submitted:', formData);
  };

  const whyHostReasons = [
    {
      icon: Heart,
      title: 'Community Impact',
      description: 'Bring life-saving blood donation directly to your community, making it convenient for donors who might not otherwise participate.',
      image: `${BASE_URL}images/hero_banner_2_2.jpg`
    },
    {
      icon: Users,
      title: 'Engage Your Community',
      description: 'Create a sense of unity and purpose among your organisation members while supporting a vital healthcare need.',
      image: `${BASE_URL}images/donation_process_1_2.jpg`
    },
    {
      icon: Building,
      title: 'Organized Support',
      description: 'Our experienced team handles all the technical aspects, from equipment setup to donor screening and collection.',
      image: `${BASE_URL}images/medical_theme_1_3.jpeg`
    }
  ];

  const planningChecklist = [
    {
      category: 'Host organisation Responsibilities',
      items: [
        'Select date and secure appropriate venue (gym, cafeteria, or large open space)',
        'Recruit volunteers for registration, scheduling, and refreshments',
        'Promote the drive through newsletters, bulletin boards, email, and social media',
        'Use online scheduling tools and send appointment reminders',
        'Engage partners for incentives and thank-you gifts',
        'Coordinate volunteer shifts and responsibilities'
      ]
    },
    {
      category: 'Blood Center Responsibilities',
      items: [
        'Provide comprehensive guidance and detailed checklists',
        'Supply all templates, tools, and online scheduling systems (RapidPass)',
        'Advise on optimal room setup, privacy requirements, and logistics',
        'Train staff for confidential donor screening and safe blood collection',
        'Handle all blood collection, processing, and distribution',
        'Provide post-drive results and performance feedback'
      ]
    }
  ];

  const timeline = [
    {
      period: '12 Weeks Before',
      title: 'Initial Planning',
      tasks: [
        'Choose date and confirm venue availability',
        'Contact blood center to discuss requirements',
        'Recruit volunteer leadership team',
        'Initial promotion planning'
      ]
    },
    {
      period: '2 Weeks Before',
      title: 'Promotion & Scheduling',
      tasks: [
        'Launch full promotion campaign',
        'Open online scheduling slots',
        'Send reminder messages to potential donors',
        'Confirm volunteer roster and training'
      ]
    },
    {
      period: '1 Week Before',
      title: 'Final Preparations',
      tasks: [
        'Finalize room setup and logistics',
        'Send reminder messages to scheduled donors',
        'Prepare refreshment area and supplies',
        'Brief all volunteers on their roles'
      ]
    },
    {
      period: 'Day Before',
      title: 'Final Checks',
      tasks: [
        'Verify all staffing and equipment',
        'Send final reminder messages',
        'Prepare signage and welcome materials',
        'Brief volunteers on schedule'
      ]
    },
    {
      period: 'Drive Day',
      title: 'Execution',
      tasks: [
        'Arrive early for setup with blood center team',
        'Manage registration and donor flow',
        'Provide refreshments and donor comfort',
        'Coordinate with blood center staff'
      ]
    },
    {
      period: 'Post-Drive',
      title: 'Follow-up',
      tasks: [
        'Collect results and donor feedback',
        'Thank all participants and volunteers',
        'Share outcomes with your organisation',
        'Debrief with blood center for future improvements'
      ]
    }
  ];

  const upcomingCamps = [
    {
      date: '2025-11-20',
      title: 'City Hall Community Drive',
      location: 'Municipal Building, Downtown',
      time: '10:00 AM - 4:00 PM',
      organizer: 'City Government',
      spotsAvailable: 45,
      image: `${BASE_URL}images/hero_banner_1_0.jpg`
    },
    {
      date: '2025-12-05',
      title: 'Navjeevan Blood Center',
      location: 'Student Union Building',
      time: '9:00 AM - 5:00 PM',
      organizer: 'State University',
      spotsAvailable: 120,
      image: `${BASE_URL}images/donation_process_1_2.jpg`
    },
    {
      date: '2025-12-15',
      title: 'Corporate Holiday Blood Drive',
      location: 'Tech Park Conference Center',
      time: '10:00 AM - 3:00 PM',
      organizer: 'Tech Industries Inc.',
      spotsAvailable: 75,
      image: `${BASE_URL}images/hero_banner_2_6.jpg`
    }
  ];

  const pastCamps = [
    `${BASE_URL}images/hero_banner_1_0.jpg`,
    `${BASE_URL}images/hero_banner_2_2.jpg`,
    `${BASE_URL}images/donation_process_1_2.jpg`,
    `${BASE_URL}images/donation_process_2_2.jpg`,
    `${BASE_URL}images/hero_banner_2_6.jpg`,
    `${BASE_URL}images/donation_process_2_4.jpg`,
    `${BASE_URL}images/hero_banner_2_8.jpg`,
    `${BASE_URL}images/donation_process_1_5.jpg`
  ];

  return (
    <div className="min-h-screen">
      {/* Page Hero */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-title font-bold text-neutral-900 mb-4">Organize a Blood Donation Camp</h1>
          <p className="text-body-lg text-neutral-600 max-w-3xl mx-auto">
            Bring life-saving blood donation directly to your community. Our comprehensive support makes hosting a blood drive simple and impactful.
          </p>
        </div>
      </section>

      {/* Why Host a Camp */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Why Host a Blood Donation Camp?</h2>
            <p className="text-body-lg text-neutral-600">
              Create meaningful impact in your community with our full support
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {whyHostReasons.map((reason, index) => {
              const IconComponent = reason.icon;
              return (
                <Card key={index} hover>
                  <CardContent className="p-0">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={reason.image} 
                        alt={reason.title} 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-normal"
                      />
                    </div>
                    <div className="p-8">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                        <IconComponent className="h-6 w-6 text-primary-600" />
                      </div>
                      <CardTitle>{reason.title}</CardTitle>
                      <p className="text-body text-neutral-600 mt-4">{reason.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Planning Checklist */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Planning Checklist</h2>
            <p className="text-body-lg text-neutral-600">
              Clear responsibilities for a successful blood drive
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {planningChecklist.map((section, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-primary-700">{section.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-semantic-success mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-body text-neutral-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Guide */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Timeline Guide</h2>
            <p className="text-body-lg text-neutral-600">
              Step-by-step timeline for planning and executing your blood drive
            </p>
          </div>
          <div className="space-y-8">
            {timeline.map((phase, index) => (
              <Card key={index} className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-l-lg"></div>
                <CardContent className="pl-8">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="md:w-1/4">
                      <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-small font-medium inline-block mb-2">
                        {phase.period}
                      </div>
                      <h3 className="text-subtitle font-semibold text-neutral-900">{phase.title}</h3>
                    </div>
                    <div className="md:w-3/4">
                      <ul className="space-y-2">
                        {phase.tasks.map((task, taskIndex) => (
                          <li key={taskIndex} className="flex items-start">
                            <Clock className="h-4 w-4 text-primary-600 mr-2 mt-1 flex-shrink-0" />
                            <span className="text-body text-neutral-700">{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Camps */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Upcoming Camps</h2>
            <p className="text-body-lg text-neutral-600">
              Join these scheduled blood drives in our community
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingCamps.map((camp, index) => (
              <Card key={index} hover>
                <CardContent className="p-0">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={camp.image} 
                      alt={camp.title} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-normal"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-primary-600 mb-2">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span className="text-small font-medium">
                        {new Date(camp.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <h3 className="text-subtitle font-semibold text-neutral-900 mb-2">{camp.title}</h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-body text-neutral-600">
                        <MapPin className="h-4 w-4 mr-2" />
                        <span>{camp.location}</span>
                      </div>
                      <div className="flex items-center text-body text-neutral-600">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>{camp.time}</span>
                      </div>
                      <div className="flex items-center text-body text-neutral-600">
                        <Users className="h-4 w-4 mr-2" />
                        <span>Organizer: {camp.organizer}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-semantic-success">
                        <Star className="h-4 w-4 mr-1" />
                        <span className="text-small font-medium">{camp.spotsAvailable} spots available</span>
                      </div>
                      <Link to="/contact" state={{ scrollTo: 'contact-form', camp: camp.title }}>
                        <Button size="sm">Join Camp</Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Past Camps Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Recent Success Stories</h2>
            <p className="text-body-lg text-neutral-600">
              Highlights from our recent blood donation camps
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pastCamps.map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg">
                <img 
                  src={image} 
                  alt={`Past camp ${index + 1}`} 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-normal"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request a Camp */}
      <section className="py-24 bg-primary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Request a Blood Donation Camp</h2>
            <p className="text-body-lg text-neutral-600">
              Ready to organize a blood drive? Fill out this form and our team will contact you within 48 hours.
            </p>
          </div>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="organizationName" className="block text-small font-medium text-neutral-700 mb-2">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="contactName" className="block text-small font-medium text-neutral-700 mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-small font-medium text-neutral-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-small font-medium text-neutral-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="eventDate" className="block text-small font-medium text-neutral-700 mb-2">
                      Preferred Event Date *
                    </label>
                    <input
                      type="date"
                      id="eventDate"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="expectedParticipants" className="block text-small font-medium text-neutral-700 mb-2">
                      Expected Participants
                    </label>
                    <select
                      id="expectedParticipants"
                      name="expectedParticipants"
                      value={formData.expectedParticipants}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select range</option>
                      <option value="1-25">1-25 people</option>
                      <option value="26-50">26-50 people</option>
                      <option value="51-100">51-100 people</option>
                      <option value="101-200">101-200 people</option>
                      <option value="200+">200+ people</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="venueAddress" className="block text-small font-medium text-neutral-700 mb-2">
                    Venue Address *
                  </label>
                  <input
                    type="text"
                    id="venueAddress"
                    name="venueAddress"
                    value={formData.venueAddress}
                    onChange={handleInputChange}
                    required
                    placeholder="Include room/suite details and accessibility information"
                    className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="specialRequirements" className="block text-small font-medium text-neutral-700 mb-2">
                    Special Requirements or Notes
                  </label>
                  <textarea
                    id="specialRequirements"
                    name="specialRequirements"
                    value={formData.specialRequirements}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Any specific needs, setup requirements, or additional information"
                    className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical"
                  ></textarea>
                </div>
                <div className="text-center">
                  <Button type="submit" size="lg">
                    <Send className="mr-2 h-5 w-5" />
                    Submit Camp Request
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-subtitle font-bold mb-4">Ready to Get Involved?</h2>
          <p className="text-body-lg mb-8 text-red-100">
            Register to donate or reach out to our team with any questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
